import { ChatText, EnvelopeSimple, Info } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RecipientPicker } from "./RecipientPicker";
import { FREQ_LABEL, overdueText } from "./useRecurring";
import { fmtDate } from "../serviceApi";

const pill = (on) => `px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-orange-300"}`;
const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const nearest = (opts, v) => opts.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a), opts[0]);
const minus = (iso, days) => { const [y, m, d] = iso.split("-").map(Number); const t = new Date(y, m - 1, d - days); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`; };

export function RecurringFields({ value, onChange, status, contacts, groups, note, editing = false }) {
  const leads = status.leads[value.frequency];
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setFreq = (f) => {
    const opts = status.leads[f];
    onChange({ ...value, frequency: f, lead_days: opts.includes(value.lead_days) ? value.lead_days : nearest(opts, value.lead_days), _leadNote: opts.includes(value.lead_days) ? null : `${value.lead_days} days is longer than a ${f === "weekly" ? "week" : "month"} — switched to the nearest valid option (${nearest(opts, value.lead_days)} days).` });
  };
  const chan = (c) => set("channels", value.channels.includes(c) ? value.channels.filter((x) => x !== c) : [...value.channels, c]);

  return (
    <div className="grid grid-cols-6 gap-3" data-testid="recurring-fields">
      <div className="col-span-6">
        <Label>Reminder frequency *</Label>
        <div className="flex gap-1.5 mt-1">
          {Object.entries(FREQ_LABEL).map(([k, l]) => <button key={k} type="button" onClick={() => setFreq(k)} className={pill(value.frequency === k)} data-testid={`recurring-freq-${k}`}>{l}</button>)}
        </div>
      </div>
      {editing && (
        <div className="col-span-6 sm:col-span-3">
          <Label>First scheduled occurrence <span className="font-normal text-slate-400">(series start — nothing earlier is shown)</span></Label>
          <Input type="date" value={value.first_occurrence || ""} max={value.due_date || undefined} onChange={(e) => set("first_occurrence", e.target.value)} data-testid="recurring-first-occurrence-input" />
        </div>
      )}
      <div className="col-span-6 sm:col-span-3">
        <Label>{editing ? "Next due date *" : "First scheduled occurrence *"} <span className="font-normal text-slate-400">({editing ? "next time the service is due" : "first time this service should occur"})</span></Label>
        <Input type="date" value={value.due_date} onChange={(e) => onChange(editing ? { ...value, due_date: e.target.value } : { ...value, due_date: e.target.value, first_occurrence: e.target.value })} data-testid="recurring-due-date-input" />
      </div>
      <div className="col-span-6 sm:col-span-3">
        <Label>Remind days before * <span className="font-normal text-slate-400">(when it is sent)</span></Label>
        <Select value={String(value.lead_days)} onValueChange={(v) => onChange({ ...value, lead_days: Number(v), _leadNote: null })}>
          <SelectTrigger data-testid="recurring-lead-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[15, 30, 60, 90, ...leads.filter((n) => n < 15)].map((n) => (
              <SelectItem key={n} value={String(n)} disabled={!leads.includes(n)} data-testid={`recurring-lead-${n}`}>{n} days before{!leads.includes(n) ? ` — too long for ${value.frequency}` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(value._leadNote || value.due_date) && (
        <div className="col-span-6 text-xs flex items-start gap-1.5 text-slate-600" data-testid="recurring-send-hint">
          <Info size={14} className="text-orange-500 mt-0.5 shrink-0" />
          <span>{value._leadNote && <b className="text-amber-700">{value._leadNote} </b>}{value.due_date && (value.due_date < todayIso()
                ? <b className="text-rose-600" data-testid="recurring-past-due-note">{fmtDate(value.due_date)} has already passed — this will be saved as Overdue. Nothing is sent now. {overdueText(status.overdue)} until it is marked completed.</b>
                : <>Service due {fmtDate(value.due_date)} — reminder goes out {fmtDate(minus(value.due_date, value.lead_days))} at 9:00 AM Eastern. If it becomes overdue: {overdueText(status.overdue).toLowerCase()} until completed.</>)}</span>
        </div>
      )}
      <div className="col-span-6">
        <Label>Notification method *</Label>
        <div className="flex gap-1.5 mt-1">
          <button type="button" onClick={() => chan("sms")} className={pill(value.channels.includes("sms"))} data-testid="recurring-channel-sms"><ChatText size={12} className="inline mr-1" />SMS</button>
          <button type="button" onClick={() => chan("email")} className={pill(value.channels.includes("email"))} data-testid="recurring-channel-email"><EnvelopeSimple size={12} className="inline mr-1" />Email</button>
        </div>
        {value.channels.includes("sms") && (
          <div className="text-[11px] text-slate-500 mt-1" data-testid="recurring-sms-note">
            Texts only go to contacts who replied YES to an SMS invite.{!status.sms_ready && <b className="text-amber-700"> SMS is Blocked/Pending carrier (A2P) approval — texts are logged as blocked, not sent, until approved.</b>}
          </div>
        )}
      </div>
      <div className="col-span-6">
        <Label>Notify contacts *</Label>
        <RecipientPicker value={value} onChange={onChange} contacts={contacts} groups={groups} />
      </div>
      {note && <div className="col-span-6 text-[11px] text-slate-500">{note}</div>}
    </div>
  );
}

export const recurringMissing = (v) => [!v.due_date && "first scheduled occurrence", !v.channels.length && "notification method", !(v.contact_ids.length || v.group_ids.length) && "contacts to notify"].filter(Boolean);
export const scheduleBody = (v) => ({ frequency: v.frequency, due_date: v.due_date, first_occurrence: v.first_occurrence || null, lead_days: v.lead_days, channels: v.channels, contact_ids: v.contact_ids, group_ids: v.group_ids });
